import * as THREE from 'three';
import { WizardType, WIZARD_CONFIGS } from '../wizards/WizardTypes';
import { SpellType } from '../game/CooldownManager';

/**
 * Animation state for a wizard model.
 * All animations use sine-wave procedural techniques on pre-allocated objects
 * to maintain 60fps without creating/destroying objects.
 */
interface WizardAnimState {
  /** Elapsed time accumulator for sine wave animations */
  time: number;
  /** Whether the wizard is currently casting */
  isCasting: boolean;
  /** Casting animation progress (0-1) */
  castProgress: number;
  /** The spell type being cast (affects animation style) */
  castSpellType: SpellType;
  /** Whether the wizard was recently damaged */
  isDamaged: boolean;
  /** Damage flash countdown */
  damageFlashTime: number;
  /** Strafe velocity for lean animation (-1 = left, 0 = idle, 1 = right) */
  strafeAmount: number;
  /** References to key parts for animation */
  body: THREE.Mesh | null;
  staff: THREE.Object3D | null;
  staffOrb: THREE.Mesh | null;
  head: THREE.Mesh | null;
  hat: THREE.Object3D | null;
  leftArm: THREE.Mesh | null;
  flameParticles: THREE.Mesh[];
  crystalCrown: THREE.Mesh[];
  ghostWisps: THREE.Mesh[];
  floatingLeaves: THREE.Mesh[];
  hourglassTop: THREE.Mesh | null;
  hourglassBottom: THREE.Mesh | null;
  clockGears: THREE.Mesh[];
  /** Original positions for resetting after animation */
  staffOrigY: number;
  staffOrigZ: number;
  bodyOrigY: number;
  /** All emissive materials for damage flash */
  allMaterials: THREE.MeshStandardMaterial[];
  /** Original emissive colors for restoring after flash */
  originalEmissive: { material: THREE.MeshStandardMaterial; color: THREE.Color; intensity: number }[];
}

export class WizardModel {
  private group: THREE.Group;
  private animState: WizardAnimState;

  constructor(wizardType: WizardType) {
    this.group = new THREE.Group();
    this.animState = {
      time: 0,
      isCasting: false,
      castProgress: 0,
      castSpellType: 'fireball',
      isDamaged: false,
      damageFlashTime: 0,
      strafeAmount: 0,
      body: null,
      staff: null,
      staffOrb: null,
      head: null,
      hat: null,
      leftArm: null,
      flameParticles: [],
      crystalCrown: [],
      ghostWisps: [],
      floatingLeaves: [],
      hourglassTop: null,
      hourglassBottom: null,
      clockGears: [],
      staffOrigY: 0,
      staffOrigZ: 0,
      bodyOrigY: 0,
      allMaterials: [],
      originalEmissive: [],
    };
    this.buildModel(wizardType);
  }

  getGroup(): THREE.Group {
    return this.group;
  }

  /** Trigger the casting animation with a spell-specific style. Resets after ~0.6 seconds. */
  triggerCast(spellType: SpellType = 'fireball'): void {
    this.animState.isCasting = true;
    this.animState.castProgress = 0;
    this.animState.castSpellType = spellType;
  }

  /** Trigger the damage flash effect. */
  triggerDamage(): void {
    this.animState.isDamaged = true;
    this.animState.damageFlashTime = 0.4; // 0.4 second flash
  }

  /** Set the strafe amount for lean/sway animation. -1 = full left, 0 = idle, 1 = full right. */
  setStrafeAmount(amount: number): void {
    this.animState.strafeAmount = amount;
  }

  /**
   * Update idle + active animations. Call every frame.
   * Uses only property mutations on existing objects -- no allocations.
   */
  update(delta: number): void {
    const s = this.animState;
    s.time += delta;
    const t = s.time;

    // --- Strafe lean animation ---
    const strafeLean = s.strafeAmount * 0.12; // Max ~7 degrees lean

    // --- Idle: breathing / body bob ---
    if (s.body) {
      const breathe = Math.sin(t * 1.8) * 0.03;
      s.body.position.y = s.bodyOrigY + breathe;
      // Gentle sway + strafe lean
      s.body.rotation.z = Math.sin(t * 0.7) * 0.02 + strafeLean;
    }

    // --- Idle: head tilt ---
    if (s.head) {
      s.head.rotation.z = Math.sin(t * 0.9 + 0.5) * 0.03;
      s.head.rotation.x = Math.sin(t * 0.6) * 0.02;
    }

    // --- Idle: staff bob ---
    if (s.staff) {
      if (!s.isCasting) {
        s.staff.position.y = s.staffOrigY + Math.sin(t * 1.2) * 0.04;
        s.staff.rotation.z = -0.2 + Math.sin(t * 0.8) * 0.03;
      }
    }

    // --- Idle: staff orb pulse ---
    if (s.staffOrb) {
      const pulse = 1 + Math.sin(t * 2.5) * 0.08;
      s.staffOrb.scale.set(pulse, pulse, pulse);
      const mat = s.staffOrb.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = 0.6 + Math.sin(t * 3.0) * 0.3;
    }

    // --- Idle: hat sway + strafe ---
    if (s.hat) {
      s.hat.rotation.z = Math.sin(t * 0.6 + 1.0) * 0.025 + strafeLean * 0.6;
    }

    // --- Idle: left arm sway ---
    if (s.leftArm) {
      s.leftArm.rotation.z = 0.3 + Math.sin(t * 1.0) * 0.08;
      s.leftArm.rotation.x = Math.sin(t * 0.7 + 0.3) * 0.05;
    }

    // --- Pyromancer flame particles bob ---
    for (let i = 0; i < s.flameParticles.length; i++) {
      const fp = s.flameParticles[i];
      fp.position.y += Math.sin(t * 3.0 + i * 1.3) * 0.003;
      fp.scale.setScalar(0.8 + Math.sin(t * 4.0 + i) * 0.3);
      const m = fp.material as THREE.MeshStandardMaterial;
      m.emissiveIntensity = 0.5 + Math.sin(t * 5.0 + i * 0.7) * 0.3;
    }

    // --- Frostmage crystal crown rotation ---
    for (let i = 0; i < s.crystalCrown.length; i++) {
      const c = s.crystalCrown[i];
      c.rotation.y += delta * 0.4;
      c.position.y = 3.0 + Math.sin(t * 1.5 + i * 1.2) * 0.06;
    }

    // --- Necromancer ghost wisps orbit ---
    for (let i = 0; i < s.ghostWisps.length; i++) {
      const w = s.ghostWisps[i];
      const angle = t * 0.8 + (i / s.ghostWisps.length) * Math.PI * 2;
      const radius = 0.9 + Math.sin(t * 0.5 + i) * 0.15;
      w.position.x = Math.cos(angle) * radius;
      w.position.z = Math.sin(angle) * radius;
      w.position.y = 1.8 + Math.sin(t * 1.2 + i * 1.5) * 0.3;
      const m = w.material as THREE.MeshStandardMaterial;
      m.opacity = 0.3 + Math.sin(t * 2.0 + i * 0.8) * 0.2;
    }

    // --- Druid floating leaves ---
    for (let i = 0; i < s.floatingLeaves.length; i++) {
      const leaf = s.floatingLeaves[i];
      const angle = t * 0.5 + (i / s.floatingLeaves.length) * Math.PI * 2;
      const radius = 0.8 + Math.sin(t * 0.3 + i * 0.7) * 0.2;
      leaf.position.x = Math.cos(angle) * radius;
      leaf.position.z = Math.sin(angle) * radius;
      leaf.position.y = 2.5 + Math.sin(t * 0.7 + i * 1.1) * 0.4;
      leaf.rotation.y = t * 1.5 + i;
      leaf.rotation.x = Math.sin(t * 0.8 + i) * 0.5;
    }

    // --- Chronomancer spinning hourglass + gears ---
    if (s.hourglassTop) {
      s.hourglassTop.rotation.y += delta * 1.2;
    }
    if (s.hourglassBottom) {
      s.hourglassBottom.rotation.y += delta * 1.2;
    }
    for (let i = 0; i < s.clockGears.length; i++) {
      const gear = s.clockGears[i];
      gear.rotation.z += delta * (i % 2 === 0 ? 1.5 : -1.5);
    }

    // --- Casting animation (spell-specific) ---
    if (s.isCasting) {
      s.castProgress += delta * 2.5; // ~0.4s to reach full, then decay
      if (s.castProgress < 1.0) {
        const p = s.castProgress;
        const sinP = Math.sin(p * Math.PI);

        switch (s.castSpellType) {
          // --- Fireball / Meteor: aggressive forward thrust, staff up ---
          case 'fireball':
          case 'meteor': {
            if (s.staff) {
              s.staff.position.y = s.staffOrigY + sinP * 0.8;
              s.staff.rotation.z = -0.2 - sinP * 0.5;
            }
            if (s.body) {
              s.body.rotation.x = sinP * 0.15;
            }
            if (s.leftArm) {
              s.leftArm.rotation.z = 0.3 - sinP * 0.8;
              s.leftArm.rotation.x = -sinP * 0.3;
            }
            break;
          }

          // --- Ice Shard / Blizzard: sweeping horizontal arc ---
          case 'iceShard':
          case 'blizzard': {
            if (s.staff) {
              s.staff.position.y = s.staffOrigY + sinP * 0.3;
              s.staff.rotation.z = -0.2 + Math.sin(p * Math.PI * 2) * 0.6;
            }
            if (s.body) {
              s.body.rotation.z = Math.sin(p * Math.PI * 2) * 0.08;
            }
            if (s.leftArm) {
              s.leftArm.rotation.z = 0.3 + Math.sin(p * Math.PI * 2) * 0.5;
            }
            break;
          }

          // --- Lightning: overhead slam (staff rises then snaps down) ---
          case 'lightningBolt': {
            const slamPhase = p < 0.6 ? p / 0.6 : 1.0 - (p - 0.6) / 0.4;
            const snap = p > 0.6 ? Math.sin((p - 0.6) / 0.4 * Math.PI) : 0;
            if (s.staff) {
              s.staff.position.y = s.staffOrigY + slamPhase * 1.0;
              s.staff.rotation.z = -0.2 - slamPhase * 0.6 + snap * 0.8;
            }
            if (s.body) {
              s.body.rotation.x = snap * 0.2;
            }
            if (s.leftArm) {
              s.leftArm.rotation.z = 0.3 - slamPhase * 0.5;
            }
            break;
          }

          // --- Shield / SuperShield / Reflect: defensive crouch, staff pulled close ---
          case 'shield':
          case 'superShield':
          case 'reflect': {
            if (s.staff) {
              s.staff.position.y = s.staffOrigY - sinP * 0.2;
              s.staff.position.z = s.staffOrigZ + sinP * 0.3;
              s.staff.rotation.z = -0.2 + sinP * 0.3;
            }
            if (s.body) {
              s.body.rotation.x = -sinP * 0.1;
              s.body.position.y = s.bodyOrigY - sinP * 0.1;
            }
            if (s.leftArm) {
              s.leftArm.rotation.z = 0.3 + sinP * 0.4;
              s.leftArm.rotation.x = sinP * 0.3;
            }
            break;
          }

          // --- Heal: upward channeling, arms raised ---
          case 'healSpell': {
            if (s.staff) {
              s.staff.position.y = s.staffOrigY + sinP * 0.5;
              s.staff.rotation.z = -0.2 + sinP * 0.15;
            }
            if (s.body) {
              s.body.position.y = s.bodyOrigY + sinP * 0.08;
            }
            if (s.leftArm) {
              s.leftArm.rotation.z = 0.3 - sinP * 0.7;
              s.leftArm.rotation.x = -sinP * 0.4;
            }
            break;
          }

          // --- Teleport: quick body spin ---
          case 'teleport': {
            if (s.body) {
              s.body.rotation.y = p * Math.PI * 2;
            }
            if (s.staff) {
              s.staff.position.y = s.staffOrigY + sinP * 0.3;
            }
            break;
          }

          // --- Default: generic cast (fallback) ---
          default: {
            if (s.staff) {
              s.staff.position.y = s.staffOrigY + sinP * 0.6;
              s.staff.rotation.z = -0.2 - sinP * 0.4;
            }
            if (s.body) {
              s.body.rotation.x = sinP * 0.1;
            }
            if (s.leftArm) {
              s.leftArm.rotation.z = 0.3 - sinP * 0.6;
            }
            break;
          }
        }
      } else {
        // Casting complete, reset
        s.isCasting = false;
        s.castProgress = 0;
        if (s.body) {
          s.body.rotation.x = 0;
          s.body.rotation.y = 0;
        }
        if (s.staff) {
          s.staff.position.z = s.staffOrigZ;
        }
      }
    }

    // --- Damage flash ---
    if (s.isDamaged) {
      s.damageFlashTime -= delta;
      const flashPhase = Math.sin(s.damageFlashTime * 25) > 0;
      for (const entry of s.originalEmissive) {
        if (flashPhase) {
          entry.material.emissive.set(0xFF2222);
          entry.material.emissiveIntensity = 2.0;
        } else {
          entry.material.emissive.copy(entry.color);
          entry.material.emissiveIntensity = entry.intensity;
        }
      }
      if (s.damageFlashTime <= 0) {
        s.isDamaged = false;
        // Restore originals
        for (const entry of s.originalEmissive) {
          entry.material.emissive.copy(entry.color);
          entry.material.emissiveIntensity = entry.intensity;
        }
      }
    }
  }

  /** Properly dispose all geometries and materials. */
  dispose(): void {
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material?.dispose();
        }
      }
      if (child instanceof THREE.Light) {
        child.dispose?.();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Material helper -- tracks materials for damage flash
  // ---------------------------------------------------------------------------
  private makeMat(params: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial(params);
    this.animState.allMaterials.push(mat);
    this.animState.originalEmissive.push({
      material: mat,
      color: mat.emissive.clone(),
      intensity: mat.emissiveIntensity,
    });
    return mat;
  }

  // ---------------------------------------------------------------------------
  // Build the right wizard
  // ---------------------------------------------------------------------------
  private buildModel(wizardType: WizardType): void {
    const config = WIZARD_CONFIGS[wizardType];
    const color = new THREE.Color(config.color);
    const darkerColor = color.clone().multiplyScalar(0.6);

    switch (wizardType) {
      case 'pyromancer':
        this.buildPyromancer(color, darkerColor);
        break;
      case 'frostmage':
        this.buildFrostmage(color, darkerColor);
        break;
      case 'battlemage':
        this.buildBattlemage(color, darkerColor);
        break;
      case 'necromancer':
        this.buildNecromancer(color, darkerColor);
        break;
      case 'druid':
        this.buildDruid(color, darkerColor);
        break;
      case 'chronomancer':
        this.buildChronomancer(color, darkerColor);
        break;
      case 'archmage':
      default:
        this.buildArchmage(color, darkerColor);
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Pyromancer
  // ---------------------------------------------------------------------------
  private buildPyromancer(color: THREE.Color, darkerColor: THREE.Color): void {
    const g = this.group;
    const s = this.animState;

    // Robe
    const robe = new THREE.Mesh(
      new THREE.ConeGeometry(0.8, 2.2, 12),
      this.makeMat({ color: darkerColor, roughness: 0.7, metalness: 0.1, emissive: new THREE.Color(0x331100), emissiveIntensity: 0.3 })
    );
    robe.position.y = 1.1;
    robe.name = 'wizardBody';
    g.add(robe);
    s.body = robe;
    s.bodyOrigY = 1.1;

    // Flame patterns on robe
    for (let i = 0; i < 8; i++) {
      const fp = new THREE.Mesh(
        new THREE.ConeGeometry(0.12, 0.3, 6),
        this.makeMat({ color: 0xFF6600, emissive: new THREE.Color(0xFF3300), emissiveIntensity: 0.6 })
      );
      const angle = (i / 8) * Math.PI * 2;
      fp.position.set(Math.cos(angle) * 0.65, 0.8 + Math.random() * 0.6, Math.sin(angle) * 0.65);
      fp.rotation.x = Math.random() * 0.5;
      g.add(fp);
      s.flameParticles.push(fp);
    }

    // Torso
    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.45, 0.8, 12),
      this.makeMat({ color, roughness: 0.6, metalness: 0.2, emissive: color, emissiveIntensity: 0.15 })
    );
    torso.position.y = 2.0;
    g.add(torso);

    // Belt
    const belt = new THREE.Mesh(
      new THREE.TorusGeometry(0.42, 0.08, 8, 16),
      this.makeMat({ color: 0x2a1a0a, roughness: 0.8, metalness: 0.3 })
    );
    belt.position.y = 1.7;
    belt.rotation.x = Math.PI / 2;
    g.add(belt);

    // Left arm
    const leftArm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.1, 0.8, 8),
      this.makeMat({ color: 0xFFDDCC, roughness: 0.9 })
    );
    leftArm.position.set(-0.45, 1.9, 0);
    leftArm.rotation.z = 0.3;
    g.add(leftArm);
    s.leftArm = leftArm;

    // Head
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 16, 16),
      this.makeMat({ color: 0xFFDDCC, roughness: 0.8, metalness: 0 })
    );
    head.position.y = 2.6;
    g.add(head);
    s.head = head;

    // Eyes
    const eyeMat = this.makeMat({ color: 0xFF4400, emissive: new THREE.Color(0xFF2200), emissiveIntensity: 1.0 });
    const eyeGeo = new THREE.SphereGeometry(0.05, 8, 8);
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.1, 2.65, 0.25);
    g.add(leftEye);
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.1, 2.65, 0.25);
    g.add(rightEye);

    // Hat base + cone
    const hatBaseMat = this.makeMat({ color, roughness: 0.5, metalness: 0.2 });
    const hatBase = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.45, 0.15, 12), hatBaseMat);
    hatBase.position.y = 2.95;
    g.add(hatBase);

    const hatGroup = new THREE.Group();
    const hat = new THREE.Mesh(
      new THREE.ConeGeometry(0.45, 1.0, 12),
      this.makeMat({ color, roughness: 0.5, metalness: 0.2, emissive: color, emissiveIntensity: 0.2 })
    );
    hat.position.y = 3.5;
    hatGroup.add(hat);
    g.add(hatGroup);
    s.hat = hatGroup;

    // Flame on hat tip
    const flame = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 8, 8),
      this.makeMat({ color: 0xFFAA00, emissive: new THREE.Color(0xFF6600), emissiveIntensity: 1.0 })
    );
    flame.position.y = 4.1;
    g.add(flame);
    s.flameParticles.push(flame);

    // Small flame particles around hat tip
    for (let i = 0; i < 5; i++) {
      const particle = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 6, 6),
        this.makeMat({ color: 0xFFAA00, emissive: new THREE.Color(0xFF6600), emissiveIntensity: 0.8, transparent: true, opacity: 0.7 })
      );
      const angle = (i / 5) * Math.PI * 2;
      particle.position.set(Math.cos(angle) * 0.25, 4.0 + Math.random() * 0.2, Math.sin(angle) * 0.25);
      g.add(particle);
      s.flameParticles.push(particle);
    }

    // Staff
    const staffGroup = new THREE.Group();
    const staff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.07, 2.5, 12),
      this.makeMat({ color: 0x4a2a0a, roughness: 0.8, metalness: 0.1 })
    );
    staff.position.set(0.6, 1.5, 0);
    staff.rotation.z = -0.2;
    staffGroup.add(staff);

    // Staff ornament rings
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.08, 0.02, 8, 12),
        this.makeMat({ color: 0x8B4513, roughness: 0.6, metalness: 0.4 })
      );
      ring.position.set(0.6, 1.0 + i * 0.6, 0);
      ring.rotation.z = -0.2;
      staffGroup.add(ring);
    }
    g.add(staffGroup);
    s.staff = staffGroup;
    s.staffOrigY = 0;

    // Fire orb on staff
    const orb = new THREE.Mesh(
      new THREE.SphereGeometry(0.25, 16, 16),
      this.makeMat({ color: 0xFF4400, emissive: new THREE.Color(0xFF2200), emissiveIntensity: 0.9, transparent: true, opacity: 0.9 })
    );
    orb.position.set(0.75, 2.9, 0);
    staffGroup.add(orb);
    s.staffOrb = orb;

    // Inner core
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(0.15, 12, 12),
      this.makeMat({ color: 0xFFFF00, emissive: new THREE.Color(0xFFFF00), emissiveIntensity: 1.2 })
    );
    core.position.set(0.75, 2.9, 0);
    staffGroup.add(core);

    // Point lights
    const glow = new THREE.PointLight(0xFF4400, 0.8, 4);
    glow.position.set(0.75, 2.9, 0);
    g.add(glow);
    const flameGlow = new THREE.PointLight(0xFF6600, 0.5, 2);
    flameGlow.position.set(0, 4.1, 0);
    g.add(flameGlow);
  }

  // ---------------------------------------------------------------------------
  // Frostmage
  // ---------------------------------------------------------------------------
  private buildFrostmage(color: THREE.Color, darkerColor: THREE.Color): void {
    const g = this.group;
    const s = this.animState;

    // Robe
    const robe = new THREE.Mesh(
      new THREE.ConeGeometry(0.9, 2.4, 6),
      this.makeMat({ color: darkerColor, roughness: 0.3, metalness: 0.5, transparent: true, opacity: 0.9 })
    );
    robe.position.y = 1.2;
    robe.name = 'wizardBody';
    g.add(robe);
    s.body = robe;
    s.bodyOrigY = 1.2;

    // Torso
    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.5, 0.9, 6),
      this.makeMat({ color, roughness: 0.2, metalness: 0.6, emissive: new THREE.Color(0x224466), emissiveIntensity: 0.2 })
    );
    torso.position.y = 2.1;
    g.add(torso);

    // Head
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 16, 16),
      this.makeMat({ color: 0xDDEEFF, roughness: 0.7, metalness: 0.1 })
    );
    head.position.y = 2.65;
    g.add(head);
    s.head = head;

    // Hood
    const hatGroup = new THREE.Group();
    const hood = new THREE.Mesh(
      new THREE.ConeGeometry(0.5, 0.9, 6),
      this.makeMat({ color, roughness: 0.1, metalness: 0.7, transparent: true, opacity: 0.85 })
    );
    hood.position.y = 3.2;
    hatGroup.add(hood);
    g.add(hatGroup);
    s.hat = hatGroup;

    // Ice crystal crown
    for (let i = 0; i < 5; i++) {
      const crystal = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.12),
        this.makeMat({ color: 0xAADDFF, emissive: new THREE.Color(0x4488FF), emissiveIntensity: 0.5, transparent: true, opacity: 0.9 })
      );
      const angle = (i / 5) * Math.PI * 2;
      crystal.position.set(Math.cos(angle) * 0.35, 3.0, Math.sin(angle) * 0.35);
      crystal.rotation.y = angle;
      g.add(crystal);
      s.crystalCrown.push(crystal);
    }

    // Staff
    const staffGroup = new THREE.Group();
    const staff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.06, 2.6, 6),
      this.makeMat({ color: 0xAABBCC, roughness: 0.2, metalness: 0.8 })
    );
    staff.position.set(0.6, 1.5, 0);
    staff.rotation.z = -0.2;
    staffGroup.add(staff);
    g.add(staffGroup);
    s.staff = staffGroup;
    s.staffOrigY = 0;

    // Top crystal on staff
    const topCrystal = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.25),
      this.makeMat({ color: 0x88CCFF, emissive: new THREE.Color(0x4488FF), emissiveIntensity: 0.6, transparent: true, opacity: 0.85 })
    );
    topCrystal.position.set(0.72, 3.0, 0);
    topCrystal.rotation.set(0.3, 0.5, 0.2);
    staffGroup.add(topCrystal);
    s.staffOrb = topCrystal;

    // Glow
    const glow = new THREE.PointLight(0x4488FF, 0.4, 3);
    glow.position.set(0.72, 3.0, 0);
    g.add(glow);
  }

  // ---------------------------------------------------------------------------
  // Battlemage
  // ---------------------------------------------------------------------------
  private buildBattlemage(color: THREE.Color, darkerColor: THREE.Color): void {
    const g = this.group;
    const s = this.animState;

    // Robe
    const robe = new THREE.Mesh(
      new THREE.ConeGeometry(0.85, 2.0, 8),
      this.makeMat({ color: darkerColor, roughness: 0.5, metalness: 0.4 })
    );
    robe.position.y = 1.0;
    robe.name = 'wizardBody';
    g.add(robe);
    s.body = robe;
    s.bodyOrigY = 1.0;

    // Armored torso
    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.45, 0.5, 1.0, 8),
      this.makeMat({ color, roughness: 0.4, metalness: 0.6 })
    );
    torso.position.y = 2.0;
    g.add(torso);

    // Shoulder pauldrons
    const pauldronGeo = new THREE.SphereGeometry(0.25, 8, 8, 0, Math.PI);
    const pauldronMat = this.makeMat({ color, roughness: 0.3, metalness: 0.7 });
    const leftPauldron = new THREE.Mesh(pauldronGeo, pauldronMat);
    leftPauldron.position.set(-0.5, 2.3, 0);
    leftPauldron.rotation.z = Math.PI / 2;
    g.add(leftPauldron);

    const rightPauldron = new THREE.Mesh(pauldronGeo, pauldronMat);
    rightPauldron.position.set(0.5, 2.3, 0);
    rightPauldron.rotation.z = -Math.PI / 2;
    g.add(rightPauldron);

    // Head
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.28, 16, 16),
      this.makeMat({ color: 0xFFDDCC, roughness: 0.8, metalness: 0 })
    );
    head.position.y = 2.65;
    g.add(head);
    s.head = head;

    // Battle helm
    const hatGroup = new THREE.Group();
    const helm = new THREE.Mesh(
      new THREE.SphereGeometry(0.35, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2),
      this.makeMat({ color, roughness: 0.3, metalness: 0.8 })
    );
    helm.position.y = 2.8;
    hatGroup.add(helm);

    // Helm crest
    const crest = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.4, 0.5),
      this.makeMat({ color: 0xFFD700, roughness: 0.4, metalness: 0.6 })
    );
    crest.position.y = 3.1;
    hatGroup.add(crest);
    g.add(hatGroup);
    s.hat = hatGroup;

    // Staff / mace
    const staffGroup = new THREE.Group();
    const staff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.08, 2.2, 8),
      this.makeMat({ color: 0x666666, roughness: 0.4, metalness: 0.7 })
    );
    staff.position.set(0.65, 1.4, 0);
    staff.rotation.z = -0.15;
    staffGroup.add(staff);
    g.add(staffGroup);
    s.staff = staffGroup;
    s.staffOrigY = 0;

    // Shield orb on staff
    const orb = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.22),
      this.makeMat({ color: 0x44FF44, emissive: new THREE.Color(0x22AA22), emissiveIntensity: 0.5 })
    );
    orb.position.set(0.75, 2.7, 0);
    staffGroup.add(orb);
    s.staffOrb = orb;

    // Glow
    const glow = new THREE.PointLight(0x44FF44, 0.4, 3);
    glow.position.set(0.75, 2.7, 0);
    g.add(glow);
  }

  // ---------------------------------------------------------------------------
  // Archmage
  // ---------------------------------------------------------------------------
  private buildArchmage(color: THREE.Color, darkerColor: THREE.Color): void {
    const g = this.group;
    const s = this.animState;

    // Robe
    const robe = new THREE.Mesh(
      new THREE.ConeGeometry(0.9, 2.3, 12),
      this.makeMat({ color: darkerColor, roughness: 0.8, metalness: 0.1 })
    );
    robe.position.y = 1.15;
    robe.name = 'wizardBody';
    g.add(robe);
    s.body = robe;
    s.bodyOrigY = 1.15;

    // Torso
    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.45, 0.85, 12),
      this.makeMat({ color, roughness: 0.7, metalness: 0.1 })
    );
    torso.position.y = 2.05;
    g.add(torso);

    // Golden sash
    const sash = new THREE.Mesh(
      new THREE.TorusGeometry(0.4, 0.05, 8, 16, Math.PI),
      this.makeMat({ color: 0xFFD700, roughness: 0.4, metalness: 0.6 })
    );
    sash.position.set(0, 2.0, 0.1);
    sash.rotation.x = Math.PI / 2;
    g.add(sash);

    // Head
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 16, 16),
      this.makeMat({ color: 0xFFDDCC, roughness: 0.8, metalness: 0 })
    );
    head.position.y = 2.6;
    g.add(head);
    s.head = head;

    // Beard
    const beard = new THREE.Mesh(
      new THREE.ConeGeometry(0.2, 0.5, 8),
      this.makeMat({ color: 0xCCCCCC, roughness: 0.9, metalness: 0 })
    );
    beard.position.set(0, 2.2, 0.15);
    beard.rotation.x = Math.PI;
    g.add(beard);

    // Hat
    const hatGroup = new THREE.Group();
    const hatBrim = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.6, 0.1, 16),
      this.makeMat({ color, roughness: 0.7, metalness: 0.1 })
    );
    hatBrim.position.y = 2.95;
    hatGroup.add(hatBrim);

    const hatTop = new THREE.Mesh(
      new THREE.ConeGeometry(0.45, 1.2, 12),
      this.makeMat({ color, roughness: 0.7, metalness: 0.1 })
    );
    hatTop.position.y = 3.55;
    hatGroup.add(hatTop);

    // Stars on hat
    const starGeo = new THREE.SphereGeometry(0.06, 4, 4);
    const starMat = this.makeMat({ color: 0xFFD700, emissive: new THREE.Color(0xFFD700), emissiveIntensity: 0.6 });
    for (let i = 0; i < 4; i++) {
      const star = new THREE.Mesh(starGeo, starMat);
      const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
      star.position.set(Math.cos(angle) * 0.3, 3.2 + i * 0.2, Math.sin(angle) * 0.3);
      hatGroup.add(star);
    }
    g.add(hatGroup);
    s.hat = hatGroup;

    // Staff
    const staffGroup = new THREE.Group();
    const staff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.07, 2.8, 8),
      this.makeMat({ color: 0x5a3a1a, roughness: 0.8, metalness: 0.1 })
    );
    staff.position.set(0.6, 1.6, 0);
    staff.rotation.z = -0.2;
    staffGroup.add(staff);
    g.add(staffGroup);
    s.staff = staffGroup;
    s.staffOrigY = 0;

    // Magical orb
    const orb = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 16, 16),
      this.makeMat({ color: 0xAA88FF, emissive: new THREE.Color(0x6644AA), emissiveIntensity: 0.6, transparent: true, opacity: 0.9 })
    );
    orb.position.set(0.72, 3.15, 0);
    staffGroup.add(orb);
    s.staffOrb = orb;

    // Glow
    const glow = new THREE.PointLight(0xAA88FF, 0.4, 3);
    glow.position.set(0.72, 3.15, 0);
    g.add(glow);
  }

  // ---------------------------------------------------------------------------
  // Necromancer
  // ---------------------------------------------------------------------------
  private buildNecromancer(color: THREE.Color, darkerColor: THREE.Color): void {
    const g = this.group;
    const s = this.animState;

    // Tattered robe - dark purple with ragged edges
    const robe = new THREE.Mesh(
      new THREE.ConeGeometry(0.85, 2.3, 8),
      this.makeMat({ color: darkerColor, roughness: 0.9, metalness: 0.05, emissive: new THREE.Color(0x110022), emissiveIntensity: 0.2 })
    );
    robe.position.y = 1.15;
    robe.name = 'wizardBody';
    g.add(robe);
    s.body = robe;
    s.bodyOrigY = 1.15;

    // Torso
    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.45, 0.85, 10),
      this.makeMat({ color, roughness: 0.7, metalness: 0.15, emissive: color, emissiveIntensity: 0.1 })
    );
    torso.position.y = 2.05;
    g.add(torso);

    // Bone belt
    const belt = new THREE.Mesh(
      new THREE.TorusGeometry(0.42, 0.06, 8, 16),
      this.makeMat({ color: 0xCCBB99, roughness: 0.9, metalness: 0.05 })
    );
    belt.position.y = 1.7;
    belt.rotation.x = Math.PI / 2;
    g.add(belt);

    // Left arm
    const leftArm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.11, 0.09, 0.8, 8),
      this.makeMat({ color: 0xBBAACC, roughness: 0.9 })
    );
    leftArm.position.set(-0.45, 1.9, 0);
    leftArm.rotation.z = 0.3;
    g.add(leftArm);
    s.leftArm = leftArm;

    // Head - pale/ashen
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 16, 16),
      this.makeMat({ color: 0xCCBBCC, roughness: 0.8, metalness: 0 })
    );
    head.position.y = 2.6;
    g.add(head);
    s.head = head;

    // Glowing green eyes
    const eyeMat = this.makeMat({ color: 0x44FF44, emissive: new THREE.Color(0x22FF22), emissiveIntensity: 1.5 });
    const eyeGeo = new THREE.SphereGeometry(0.05, 8, 8);
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.1, 2.65, 0.25);
    g.add(leftEye);
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.1, 2.65, 0.25);
    g.add(rightEye);

    // Hood
    const hatGroup = new THREE.Group();
    const hood = new THREE.Mesh(
      new THREE.ConeGeometry(0.48, 0.7, 8),
      this.makeMat({ color: darkerColor, roughness: 0.9, metalness: 0.05 })
    );
    hood.position.y = 3.05;
    hatGroup.add(hood);

    // Hood rim
    const hoodRim = new THREE.Mesh(
      new THREE.TorusGeometry(0.38, 0.04, 8, 12),
      this.makeMat({ color: darkerColor, roughness: 0.8 })
    );
    hoodRim.position.y = 2.8;
    hoodRim.rotation.x = Math.PI / 2;
    hatGroup.add(hoodRim);
    g.add(hatGroup);
    s.hat = hatGroup;

    // Staff with skull
    const staffGroup = new THREE.Group();
    const staff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.07, 2.6, 8),
      this.makeMat({ color: 0x2a1a2a, roughness: 0.8, metalness: 0.1 })
    );
    staff.position.set(0.6, 1.5, 0);
    staff.rotation.z = -0.2;
    staffGroup.add(staff);
    g.add(staffGroup);
    s.staff = staffGroup;
    s.staffOrigY = 0;

    // Skull on staff top
    const skull = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 10, 10),
      this.makeMat({ color: 0xDDCCBB, roughness: 0.9, metalness: 0 })
    );
    skull.position.set(0.72, 2.95, 0);
    skull.scale.set(1, 1.1, 0.9);
    staffGroup.add(skull);

    // Skull eye sockets (glowing green)
    const socketMat = this.makeMat({ color: 0x22FF22, emissive: new THREE.Color(0x22FF22), emissiveIntensity: 1.0 });
    const socketGeo = new THREE.SphereGeometry(0.04, 6, 6);
    const leftSocket = new THREE.Mesh(socketGeo, socketMat);
    leftSocket.position.set(0.67, 2.98, 0.12);
    staffGroup.add(leftSocket);
    const rightSocket = new THREE.Mesh(socketGeo, socketMat);
    rightSocket.position.set(0.77, 2.98, 0.12);
    staffGroup.add(rightSocket);

    // Green orb glow behind skull
    const orb = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 12, 12),
      this.makeMat({ color: 0x22FF44, emissive: new THREE.Color(0x22FF22), emissiveIntensity: 0.7, transparent: true, opacity: 0.5 })
    );
    orb.position.set(0.72, 2.95, 0);
    staffGroup.add(orb);
    s.staffOrb = orb;

    // Ghost wisps orbiting the body
    for (let i = 0; i < 4; i++) {
      const wisp = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 6, 6),
        this.makeMat({ color: 0x44FF66, emissive: new THREE.Color(0x22FF44), emissiveIntensity: 0.8, transparent: true, opacity: 0.4 })
      );
      const angle = (i / 4) * Math.PI * 2;
      wisp.position.set(Math.cos(angle) * 0.9, 1.8, Math.sin(angle) * 0.9);
      g.add(wisp);
      s.ghostWisps.push(wisp);
    }

    // Point lights
    const glow = new THREE.PointLight(0x22FF22, 0.5, 3);
    glow.position.set(0.72, 2.95, 0);
    g.add(glow);
    const eyeGlow = new THREE.PointLight(0x22FF22, 0.3, 2);
    eyeGlow.position.set(0, 2.65, 0.3);
    g.add(eyeGlow);
  }

  // ---------------------------------------------------------------------------
  // Druid
  // ---------------------------------------------------------------------------
  private buildDruid(_color: THREE.Color, darkerColor: THREE.Color): void {
    const g = this.group;
    const s = this.animState;

    // Earthy robe
    const robe = new THREE.Mesh(
      new THREE.ConeGeometry(0.9, 2.3, 10),
      this.makeMat({ color: darkerColor, roughness: 0.9, metalness: 0 })
    );
    robe.position.y = 1.15;
    robe.name = 'wizardBody';
    g.add(robe);
    s.body = robe;
    s.bodyOrigY = 1.15;

    // Vine patterns on robe
    for (let i = 0; i < 6; i++) {
      const vine = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.03, 0.4, 4),
        this.makeMat({ color: 0x558833, roughness: 0.9, emissive: new THREE.Color(0x223311), emissiveIntensity: 0.2 })
      );
      const angle = (i / 6) * Math.PI * 2;
      vine.position.set(Math.cos(angle) * 0.6, 0.9 + Math.random() * 0.6, Math.sin(angle) * 0.6);
      vine.rotation.z = Math.random() * 0.8 - 0.4;
      g.add(vine);
    }

    // Torso
    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.38, 0.48, 0.85, 10),
      this.makeMat({ color: new THREE.Color(0x6B4226), roughness: 0.9, metalness: 0 })
    );
    torso.position.y = 2.05;
    g.add(torso);

    // Bark-textured belt
    const belt = new THREE.Mesh(
      new THREE.TorusGeometry(0.44, 0.06, 8, 16),
      this.makeMat({ color: 0x5C3A1E, roughness: 0.95, metalness: 0 })
    );
    belt.position.y = 1.7;
    belt.rotation.x = Math.PI / 2;
    g.add(belt);

    // Left arm
    const leftArm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.1, 0.8, 8),
      this.makeMat({ color: 0xDDBBA0, roughness: 0.9 })
    );
    leftArm.position.set(-0.45, 1.9, 0);
    leftArm.rotation.z = 0.3;
    g.add(leftArm);
    s.leftArm = leftArm;

    // Head
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 16, 16),
      this.makeMat({ color: 0xDDBBA0, roughness: 0.8, metalness: 0 })
    );
    head.position.y = 2.6;
    g.add(head);
    s.head = head;

    // Amber eyes
    const eyeMat = this.makeMat({ color: 0xDD8822, emissive: new THREE.Color(0xBB6600), emissiveIntensity: 0.6 });
    const eyeGeo = new THREE.SphereGeometry(0.05, 8, 8);
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.1, 2.65, 0.25);
    g.add(leftEye);
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.1, 2.65, 0.25);
    g.add(rightEye);

    // Antler crown
    const hatGroup = new THREE.Group();
    const antlerMat = this.makeMat({ color: 0x8B7355, roughness: 0.9, metalness: 0 });
    // Left antler
    const leftAntler = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.05, 0.6, 6), antlerMat);
    leftAntler.position.set(-0.2, 3.1, 0);
    leftAntler.rotation.z = 0.4;
    hatGroup.add(leftAntler);
    const leftBranch = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.03, 0.3, 4), antlerMat);
    leftBranch.position.set(-0.35, 3.3, 0.05);
    leftBranch.rotation.z = 0.8;
    hatGroup.add(leftBranch);
    // Right antler
    const rightAntler = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.05, 0.6, 6), antlerMat);
    rightAntler.position.set(0.2, 3.1, 0);
    rightAntler.rotation.z = -0.4;
    hatGroup.add(rightAntler);
    const rightBranch = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.03, 0.3, 4), antlerMat);
    rightBranch.position.set(0.35, 3.3, 0.05);
    rightBranch.rotation.z = -0.8;
    hatGroup.add(rightBranch);
    g.add(hatGroup);
    s.hat = hatGroup;

    // Living wood staff with leafy canopy
    const staffGroup = new THREE.Group();
    const staff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.08, 2.6, 6),
      this.makeMat({ color: 0x5C3A1E, roughness: 0.9, metalness: 0 })
    );
    staff.position.set(0.6, 1.5, 0);
    staff.rotation.z = -0.2;
    staffGroup.add(staff);

    // Bark knots on staff
    for (let i = 0; i < 2; i++) {
      const knot = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 6, 6),
        this.makeMat({ color: 0x4A2A0A, roughness: 0.95 })
      );
      knot.position.set(0.6, 1.2 + i * 0.8, 0.05);
      staffGroup.add(knot);
    }
    g.add(staffGroup);
    s.staff = staffGroup;
    s.staffOrigY = 0;

    // Leafy canopy orb on staff top
    const canopy = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 8, 8),
      this.makeMat({ color: 0x44AA33, emissive: new THREE.Color(0x228822), emissiveIntensity: 0.4, transparent: true, opacity: 0.85 })
    );
    canopy.position.set(0.72, 3.0, 0);
    staffGroup.add(canopy);
    s.staffOrb = canopy;

    // Small leaves around canopy
    const leafMat = this.makeMat({ color: 0x55BB33, emissive: new THREE.Color(0x227711), emissiveIntensity: 0.3 });
    for (let i = 0; i < 4; i++) {
      const leaf = new THREE.Mesh(
        new THREE.ConeGeometry(0.06, 0.15, 4),
        leafMat
      );
      const angle = (i / 4) * Math.PI * 2;
      leaf.position.set(0.72 + Math.cos(angle) * 0.2, 3.0 + Math.sin(angle) * 0.15, Math.sin(angle) * 0.2);
      leaf.rotation.set(Math.random(), Math.random(), Math.random());
      staffGroup.add(leaf);
    }

    // Floating leaves around the body
    for (let i = 0; i < 5; i++) {
      const fLeaf = new THREE.Mesh(
        new THREE.ConeGeometry(0.05, 0.12, 4),
        this.makeMat({ color: 0x66BB44, emissive: new THREE.Color(0x338822), emissiveIntensity: 0.3, transparent: true, opacity: 0.7 })
      );
      const angle = (i / 5) * Math.PI * 2;
      fLeaf.position.set(Math.cos(angle) * 0.8, 2.5, Math.sin(angle) * 0.8);
      g.add(fLeaf);
      s.floatingLeaves.push(fLeaf);
    }

    // Warm earthy glow
    const glow = new THREE.PointLight(0x88AA44, 0.4, 3);
    glow.position.set(0.72, 3.0, 0);
    g.add(glow);
  }

  // ---------------------------------------------------------------------------
  // Chronomancer
  // ---------------------------------------------------------------------------
  private buildChronomancer(color: THREE.Color, darkerColor: THREE.Color): void {
    const g = this.group;
    const s = this.animState;

    // Flowing blue-silver robe with semi-transparent edges
    const robe = new THREE.Mesh(
      new THREE.ConeGeometry(0.85, 2.3, 12),
      this.makeMat({ color: darkerColor, roughness: 0.4, metalness: 0.4, transparent: true, opacity: 0.92 })
    );
    robe.position.y = 1.15;
    robe.name = 'wizardBody';
    g.add(robe);
    s.body = robe;
    s.bodyOrigY = 1.15;

    // Clock/gear motifs on robe
    for (let i = 0; i < 4; i++) {
      const gear = new THREE.Mesh(
        new THREE.TorusGeometry(0.1, 0.02, 6, 8),
        this.makeMat({ color: 0xCCBB88, roughness: 0.3, metalness: 0.7, emissive: new THREE.Color(0x886633), emissiveIntensity: 0.2 })
      );
      const angle = (i / 4) * Math.PI * 2;
      gear.position.set(Math.cos(angle) * 0.55, 1.0 + i * 0.25, Math.sin(angle) * 0.55);
      gear.rotation.x = Math.PI / 2 + Math.random() * 0.3;
      g.add(gear);
      s.clockGears.push(gear);
    }

    // Torso
    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.45, 0.85, 12),
      this.makeMat({ color, roughness: 0.3, metalness: 0.5, emissive: color, emissiveIntensity: 0.1 })
    );
    torso.position.y = 2.05;
    g.add(torso);

    // Clock-face belt buckle
    const buckle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.12, 0.04, 16),
      this.makeMat({ color: 0xDDCC88, roughness: 0.3, metalness: 0.8, emissive: new THREE.Color(0x887744), emissiveIntensity: 0.3 })
    );
    buckle.position.set(0, 1.7, 0.4);
    buckle.rotation.x = Math.PI / 2;
    g.add(buckle);

    // Clock hands on buckle
    const handMat = this.makeMat({ color: 0x222222, roughness: 0.5, metalness: 0.8 });
    const hourHand = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.08, 0.01), handMat);
    hourHand.position.set(0, 1.72, 0.42);
    g.add(hourHand);
    const minHand = new THREE.Mesh(new THREE.BoxGeometry(0.015, 0.1, 0.01), handMat);
    minHand.position.set(0, 1.72, 0.43);
    minHand.rotation.z = 1.2;
    g.add(minHand);

    // Left arm
    const leftArm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.1, 0.8, 8),
      this.makeMat({ color: 0xDDDDEE, roughness: 0.8 })
    );
    leftArm.position.set(-0.45, 1.9, 0);
    leftArm.rotation.z = 0.3;
    g.add(leftArm);
    s.leftArm = leftArm;

    // Head
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 16, 16),
      this.makeMat({ color: 0xDDDDEE, roughness: 0.8, metalness: 0 })
    );
    head.position.y = 2.6;
    g.add(head);
    s.head = head;

    // Silver-blue glowing eyes
    const eyeMat = this.makeMat({ color: 0x4488FF, emissive: new THREE.Color(0x2266FF), emissiveIntensity: 0.8 });
    const eyeGeo = new THREE.SphereGeometry(0.05, 8, 8);
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.1, 2.65, 0.25);
    g.add(leftEye);
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.1, 2.65, 0.25);
    g.add(rightEye);

    // Pointed hat with clock motif
    const hatGroup = new THREE.Group();
    const hatBrim = new THREE.Mesh(
      new THREE.CylinderGeometry(0.52, 0.55, 0.1, 12),
      this.makeMat({ color, roughness: 0.4, metalness: 0.4 })
    );
    hatBrim.position.y = 2.95;
    hatGroup.add(hatBrim);

    const hatTop = new THREE.Mesh(
      new THREE.ConeGeometry(0.42, 1.0, 12),
      this.makeMat({ color, roughness: 0.4, metalness: 0.4, emissive: color, emissiveIntensity: 0.15 })
    );
    hatTop.position.y = 3.45;
    hatGroup.add(hatTop);

    // Small clock face on hat
    const hatClock = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 0.02, 12),
      this.makeMat({ color: 0xEEDD99, roughness: 0.3, metalness: 0.7, emissive: new THREE.Color(0xAA9955), emissiveIntensity: 0.4 })
    );
    hatClock.position.set(0, 3.2, 0.35);
    hatClock.rotation.x = Math.PI / 2;
    hatGroup.add(hatClock);
    g.add(hatGroup);
    s.hat = hatGroup;

    // Staff with spinning hourglass top
    const staffGroup = new THREE.Group();
    const staff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.06, 2.6, 8),
      this.makeMat({ color: 0x8899AA, roughness: 0.3, metalness: 0.6 })
    );
    staff.position.set(0.6, 1.5, 0);
    staff.rotation.z = -0.2;
    staffGroup.add(staff);

    // Metal rings on staff
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.07, 0.015, 8, 12),
        this.makeMat({ color: 0xCCBB88, roughness: 0.3, metalness: 0.8 })
      );
      ring.position.set(0.6, 1.0 + i * 0.6, 0);
      ring.rotation.z = -0.2;
      staffGroup.add(ring);
    }
    g.add(staffGroup);
    s.staff = staffGroup;
    s.staffOrigY = 0;

    // Hourglass on staff top
    const hourglassMat = this.makeMat({ color: 0x88BBFF, emissive: new THREE.Color(0x4488FF), emissiveIntensity: 0.6, transparent: true, opacity: 0.8 });

    // Top cone of hourglass
    const hgTop = new THREE.Mesh(
      new THREE.ConeGeometry(0.14, 0.2, 8),
      hourglassMat
    );
    hgTop.position.set(0.72, 3.1, 0);
    hgTop.rotation.x = Math.PI; // inverted
    staffGroup.add(hgTop);
    s.hourglassTop = hgTop;

    // Bottom cone of hourglass
    const hgBottom = new THREE.Mesh(
      new THREE.ConeGeometry(0.14, 0.2, 8),
      hourglassMat
    );
    hgBottom.position.set(0.72, 2.85, 0);
    staffGroup.add(hgBottom);
    s.hourglassBottom = hgBottom;

    // Center waist of hourglass
    const hgCenter = new THREE.Mesh(
      new THREE.SphereGeometry(0.06, 8, 8),
      this.makeMat({ color: 0xAADDFF, emissive: new THREE.Color(0x6699FF), emissiveIntensity: 0.8 })
    );
    hgCenter.position.set(0.72, 2.97, 0);
    staffGroup.add(hgCenter);
    s.staffOrb = hgCenter;

    // Frame of hourglass
    const frameMat = this.makeMat({ color: 0xCCBB88, roughness: 0.3, metalness: 0.8 });
    const frameTop = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.03, 8), frameMat);
    frameTop.position.set(0.72, 3.2, 0);
    staffGroup.add(frameTop);
    const frameBottom = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.03, 8), frameMat);
    frameBottom.position.set(0.72, 2.75, 0);
    staffGroup.add(frameBottom);

    // Temporal shimmer particles
    for (let i = 0; i < 3; i++) {
      const shimmer = new THREE.Mesh(
        new THREE.SphereGeometry(0.04, 6, 6),
        this.makeMat({ color: 0xAADDFF, emissive: new THREE.Color(0x6699FF), emissiveIntensity: 0.5, transparent: true, opacity: 0.5 })
      );
      const angle = (i / 3) * Math.PI * 2;
      shimmer.position.set(0.72 + Math.cos(angle) * 0.25, 2.97, Math.sin(angle) * 0.25);
      staffGroup.add(shimmer);
    }

    // Point lights
    const glow = new THREE.PointLight(0x4488FF, 0.5, 3);
    glow.position.set(0.72, 2.97, 0);
    g.add(glow);
  }
}
